import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { trigger, style, transition, animate } from '@angular/animations';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from 'src/app/icons/icon-provider';
import { ApiService } from 'src/app/services/api.service';
import {
  getCriteria,
  getOnlyText,
  getRankingPayload,
  normaliseRanking,
  RankingCriterion,
} from '../ai-criteria.util';

/** Tab 3 — AI Insights. Calls `jobs/fetchJobRankingDetails` with a payload
 *  built from `pageFrom`, renders criteria bars + score ring + feedback
 *  buttons + strengths/gaps lists. Feedback writes via
 *  `jobs/updateFeedbacks`.
 *
 *  Works for BOTH directions:
 *    - Candidate side: `pageFrom ∈ {job-applicants, talent-pool, suggested-candidates}` → type:'JOB'
 *    - Job side:       `pageFrom ∈ {jobsApplied, suggestedJobs}`                       → type:'CANDIDATE'
 */
@Component({
  selector: 'app-cp-ai-insights',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIconComponent],
  providers: [provideIcons(APP_ICONS)],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './cp-ai-insights.component.html',
  animations: [
    // Used by the Disagree reason box (positioned with `right: 0`, so a
    // plain vertical fade is correct here).
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(-5px)' }))
      ])
    ]),
    // Used by the thank-you tooltips, which are centered via
    // `transform: translateX(-50%)`. The keyframes MUST keep that X-offset
    // in every step, otherwise the animation clobbers the centering and
    // the bubble jumps/clips (looked like the text arrived in two stages).
    trigger('tooltipFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50%) translateY(-5px)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(-50%) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(-50%) translateY(-5px)' }))
      ])
    ])
  ]
})
export class CpAiInsightsComponent implements OnChanges {
  @Input() pageFrom: string | undefined;
  @Input() jobId: string | undefined;
  @Input() candidateId: string | undefined;
  /** Visual variant. `wireframe` adds the purple-blue gradient score
   *  card, the "Match Breakdown" label, vertical divider between halves,
   *  outlined feedback buttons, etc. (matches `gigsumo_job_center_v2(16)`
   *  AI tab spec). Passed from the candidate slider only — the job
   *  slider stays on the default look until the change is reviewed. */
  @Input() variant: 'default' | 'wireframe' = 'default';

  loading: boolean = false;
  loadError: string | null = null;

  score: number = 0;
  reason: string = '';
  strengths: string[] = [];
  weaknesses: string[] = [];
  criteria: RankingCriterion[] = [];

  /** Persisted feedback (per ranking) — server returns these on refetch. */
  strengthsFeedback: string | null = null;
  weaknessesFeedback: string | null = null;
  overAllFeedback: string | null = null;

  /** Stored DISAGREE reasons. Held so a click on ANOTHER section can resend
   *  this one unchanged instead of blanking it. */
  strengthsComments: string | null = null;
  weaknessesComments: string | null = null;

  /** Local UI state for per-card thank-you tooltips. */
  lastFeedbackOn: Record<string, number> = {};

  /** Per-button "saving…" state so an inline spinner shows on the exact
   *  button being submitted (no full-page loader). `savingOverall` holds
   *  the accuracy value in flight; `savingStrength` / `savingWeakness`
   *  hold 'AGREE' | 'DISAGREE'. */
  savingOverall: string | null = null;
  savingStrength: string | null = null;
  savingWeakness: string | null = null;

  constructor(private api: ApiService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Trigger a fetch whenever the parent switches into the AI tab with a
    // real `pageFrom` + both ids available. The parent guards lazy-render
    // via `*ngIf` so this runs only when the tab is visible.
    if (this.pageFrom && this.jobId && this.candidateId) {
      this.fetch();
    }
  }

  /** Only fire the fetch once per input combo — avoid refetching on tab flip.
   *  Public so the "Retry" button in the template can reset it. */
  lastFetchKey: string | null = null;

  fetch(): void {
    const payload = getRankingPayload(this.pageFrom, this.jobId, this.candidateId);
    if (!payload) { this.loadError = 'Unknown page context'; return; }

    const key = `${payload.type}|${payload.groupType}|${payload.sourceEntityId}|${payload.targetEntityId}`;
    if (key === this.lastFetchKey) return;
    this.lastFetchKey = key;

    this.loading = true;
    this.loadError = null;

    this.api.fetchRankingDetailsWithRetry(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.code !== '00000') {
          this.loadError = 'AI insights are not available yet.';
          return;
        }
        const norm = normaliseRanking(res.data);
        if (!norm) {
          this.loadError = 'No ranking data found for this pairing.';
          return;
        }

        this.score = norm.score;
        this.reason = norm.reason;
        this.strengthsFeedback = norm.strengthsFeedback ?? null;
        this.weaknessesFeedback = norm.weaknessesFeedback ?? null;
        this.overAllFeedback = norm.overAllFeedback ?? null;
        this.strengthsComments = norm.strengthsComments ?? null;
        this.weaknessesComments = norm.weaknessesComments ?? null;

        const combined = [...norm.strengths, ...norm.weaknesses];
        // Row order locked to the approved Score Breakdown sequence:
        // Job Title → Skills → Experience → Location → Work Mode.
        this.criteria = [
          // `Job Title` row — backend already emits "Job Title Alignment"
          // / "Title Match" phrases (see `ai-criteria.util.ts:58-59`),
          // surfaced here via the `'Title'` criterion key. Bar hides on
          // its own when `getCriteria()` returns null (no match phrase).
          { label: 'Job Title',  percentage: getCriteria(combined, 'Title') },
          { label: 'Skills',     percentage: getCriteria(combined, 'Skills') },
          { label: 'Experience', percentage: getCriteria(combined, 'Experience') },
          { label: 'Location',   percentage: getCriteria(combined, 'Location') },
          { label: 'Work Mode',  percentage: getCriteria(combined, 'Work Mode') },
        ];

        this.strengths = norm.strengths.map(s => getOnlyText(s));
        this.weaknesses = norm.weaknesses.map(w => getOnlyText(w));

        // TEMP DIAGNOSTIC — feedback not carrying over between the seeker's
        // Invites / Suggested / Applied tabs. Shows WHICH level of the
        // response actually carries the saved feedback (ranking vs
        // targetEntity) and what this tab resolved from it. Remove once the
        // root cause is confirmed.
        const rk = res?.data?.rankings?.[0];
        const te = rk?.targetEntities?.[0];
        const pick = (o: any) => o ? {
          strengthsFeedback: o.strengthsFeedback,
          weaknessesFeedback: o.weaknessesFeedback,
          overAllFeedback: o.overAllFeedback,
          strengthsComments: o.strengthsComments,
          weaknessesComments: o.weaknessesComments,
        } : '(absent)';
        // Stringified, NOT an object arg — the console renders object args as
        // a collapsed `{…}`, so the values can't be copied out of the log.
        console.log('[AI-FB] READ ' + JSON.stringify({
          page: this.pageFrom,
          sent: payload,
          cacheSource: res?.data?.source,
          shape: res?.data?.ranking ? 'old: data.ranking' : (rk ? 'new: data.rankings[]' : 'unrecognised'),
          rankingLevel: pick(res?.data?.ranking ?? rk),
          targetLevel: pick(te),
          resolved: {
            strengths: this.strengthsFeedback,
            weaknesses: this.weaknessesFeedback,
            overall: this.overAllFeedback,
          },
        }, null, 1));
      },
      error: (err: any) => {
        this.loading = false;
        if (err?.timeout || err?.isProcessing) {
          this.loadError = 'AI analysis is still processing. Please try again in a moment.';
        } else {
          this.loadError = 'Failed to load AI insights.';
        }
      }
    });
  }

  /** Score ring — stroke dash for 2πr, r=28 → circumference ≈ 176. */
  get ringStroke(): string {
    const pct = Math.max(0, Math.min(100, this.score));
    const c = 176;
    return `${(pct / 100) * c} ${c}`;
  }
  get ringColor(): string {
    // Match the header/applicants match badges exactly (see .jc-v3-*-match-*
    // rules in job-center-v3.scss): green >=75, amber 40-74, red <40.
    const s = this.score;
    if (s >= 75) return '#059669';  // green
    if (s >= 40) return '#D97706';  // amber
    return '#DC2626';               // red
  }
  get barColor(): (label: string) => string {
    return (label) => {
      switch (label) {
        case 'Skills':     return '#10B981';  // green
        case 'Experience': return '#8B5CF6';  // purple
        case 'Job Title':  return '#EC4899';  // pink — distinct from
                                              // the existing palette so
                                              // the new row reads as a
                                              // separate dimension at a
                                              // glance.
        case 'Location':   return '#F59E0B';  // amber
        case 'Work Mode':  return '#3B82F6';  // blue
        default:           return '#6B7280';
      }
    };
  }

  /** Inline confirmation message shown under the accuracy options (wireframe):
   *  Accurate → "Thanks!", Somewhat → "We'll refine it.", Not accurate → "We'll improve."
   *  Persists while an option is selected. */
  get overallFeedbackMessage(): string {
    switch (this.overAllFeedback) {
      case 'ACCURATE':          return 'Thanks!';
      case 'SOMEWHAT_ACCURATE': return "We'll refine it.";
      case 'NOT_ACCURATE':      return "We'll improve.";
      default:                  return '';
    }
  }

  // ── Feedback submission ────────────────────────────────────────────
  // ONE request carries all three sections. The panel has no Save button -
  // each control writes on click - so a click updates local state and then
  // queues a single save that sends overall + strengths + weaknesses
  // together. Sending only the clicked section is what caused feedback to
  // randomly disappear: the server reads the ranking out of Elasticsearch,
  // edits it and writes it back, and Elasticsearch only makes a write
  // searchable ~1s later, so the second of three rapid calls read the
  // pre-first-call copy and wrote it back, dropping the earlier section.
  // Every request now being complete makes that impossible.

  /** Debounce window - coalesces rapid clicks into one request. */
  private static readonly FEEDBACK_DEBOUNCE_MS = 400;
  private fbTimer: any = null;
  private fbInFlight = false;
  private fbDirty = false;

  /** Mark feedback changed and schedule the single save. */
  private queueFeedbackSave(): void {
    this.fbDirty = true;
    if (this.fbTimer) clearTimeout(this.fbTimer);
    this.fbTimer = setTimeout(() => this.flushFeedback(), CpAiInsightsComponent.FEEDBACK_DEBOUNCE_MS);
  }

  /** Send the current state of all three sections as ONE request.
   *  Requests are strictly serialised - a click during a save re-fires
   *  afterwards - so an older payload can never land after a newer one. */
  private flushFeedback(): void {
    if (this.fbInFlight || !this.fbDirty) return;

    const payload: any = getRankingPayload(this.pageFrom, this.jobId, this.candidateId);
    if (!payload) return;
    payload.userId = localStorage.getItem('userId');

    // Only sections this component actually knows are sent. The server
    // leaves an omitted section exactly as stored, so "not known here" can
    // never blank a value written from another screen.
    if (this.overAllFeedback) payload.overAllFeedback = this.overAllFeedback;
    if (this.strengthsFeedback) {
      payload.strengthsFeedback = this.strengthsFeedback;
      if (this.strengthsComments) payload.strengthsComments = this.strengthsComments;
    }
    if (this.weaknessesFeedback) {
      payload.weaknessesFeedback = this.weaknessesFeedback;
      if (this.weaknessesComments) payload.weaknessesComments = this.weaknessesComments;
    }

    if (!payload.overAllFeedback && !payload.strengthsFeedback && !payload.weaknessesFeedback) {
      this.fbDirty = false;
      return;
    }

    this.fbDirty = false;
    this.fbInFlight = true;
    console.log('[AI-FB] WRITE ' + JSON.stringify({ page: this.pageFrom, payload }));

    this.api.updatePut('jobs/updateFeedbacks', payload).subscribe({
      next: (res: any) => {
        console.log('%c[AI-FB] WRITE ok', 'color:#059669;font-weight:bold', res);
        this.fbInFlight = false;
        this.clearSavingFlags();
        if (this.fbDirty) this.flushFeedback();
      },
      error: () => {
        this.fbInFlight = false;
        this.clearSavingFlags();
      }
    });
  }

  private clearSavingFlags(): void {
    this.savingOverall = null;
    this.savingStrength = null;
    this.savingWeakness = null;
  }

  /** Overall accuracy feedback: ACCURATE / SOMEWHAT_ACCURATE / NOT_ACCURATE.
   *  Re-editable — the user can switch their pick at any time; the chosen
   *  option stays highlighted with a check and shows the inline message. */
  sendOverallFeedback(value: 'ACCURATE' | 'SOMEWHAT_ACCURATE' | 'NOT_ACCURATE'): void {
    if (!getRankingPayload(this.pageFrom, this.jobId, this.candidateId)) return;

    // Optimistic: select immediately so the inline message (Thanks! / We'll
    // refine it. / We'll improve.) shows right away.
    this.savingOverall = value;
    this.overAllFeedback = value;
    this.lastFeedbackOn['overall'] = Date.now();

    this.queueFeedbackSave();
  }

  /** Per-category (strengths/weaknesses) feedback — AGREE / DISAGREE.
   *  DISAGREE carries an optional free-text `comments` reason (submitted
   *  from the reason box); AGREE has none and clears any stored one. */
  sendCategoryFeedback(source: 'STRENGTH' | 'WEAKNESS', value: 'AGREE' | 'DISAGREE', comments?: string): void {
    if (!getRankingPayload(this.pageFrom, this.jobId, this.candidateId)) return;

    if (source === 'STRENGTH') {
      this.savingStrength = value;
      this.strengthsFeedback = value;
      this.strengthsComments = value === 'DISAGREE' ? (comments ?? this.strengthsComments) : null;
    } else {
      this.savingWeakness = value;
      this.weaknessesFeedback = value;
      this.weaknessesComments = value === 'DISAGREE' ? (comments ?? this.weaknessesComments) : null;
    }
    this.lastFeedbackOn[source.toLowerCase()] = Date.now();

    this.queueFeedbackSave();
  }

  // ── Thank-you tooltip state ────────────────────────────────────────
  showTooltip: Record<string, boolean> = {};
  private tooltipTimeouts: Record<string, any> = {};

  /** Flash a "thank you" tooltip for `type`, auto-hiding after 2s. */
  displayTooltip(type: string): void {
    if (this.tooltipTimeouts[type]) clearTimeout(this.tooltipTimeouts[type]);
    this.showTooltip[type] = true;
    this.tooltipTimeouts[type] = setTimeout(() => { this.showTooltip[type] = false; }, 2000);
  }

  // ── Disagree reason-box state ──────────────────────────────────────
  showDisagreeInput: Record<string, boolean> = {};
  disagreeReason: Record<string, string> = {};

  /** Set when a reason box has to open ABOVE its button (see `placeDisagreeBox`). */
  disagreeDropUp: Record<string, boolean> = {};

  // The reason boxes sit behind *ngIf, so these resolve only once one is rendered.
  @ViewChild('strengthDisagreeInput') strengthDisagreeInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('weaknessDisagreeInput') weaknessDisagreeInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('strengthDisagreeBox') strengthDisagreeBox?: ElementRef<HTMLElement>;
  @ViewChild('weaknessDisagreeBox') weaknessDisagreeBox?: ElementRef<HTMLElement>;

  /** Open/close the reason box under a Disagree button. `type` is the UI
   *  key: 'strength' | 'weakness'. */
  toggleDisagreeInput(type: 'strength' | 'weakness'): void {
    const willOpen = !this.showDisagreeInput[type];

    // Only one reason box may be open at a time — opening this one discards
    // any other still-open box (same semantics as closeDisagreeInput).
    Object.keys(this.showDisagreeInput).forEach(k => {
      if (k !== type && this.showDisagreeInput[k]) {
        this.showDisagreeInput[k] = false;
        this.disagreeReason[k] = '';
      }
    });

    this.showDisagreeInput[type] = willOpen;
    if (willOpen) {
      this.disagreeReason[type] = '';
      // Clear before the box renders: `placeDisagreeBox` measures it in its
      // default dropped-down position, so a leftover flip from the previous
      // open would make that measurement wrong. Never cleared on close — the
      // box is still on screen during its :leave fade and would snap back.
      this.disagreeDropUp[type] = false;
      // Deferred because the box is behind *ngIf and only exists after this
      // change-detection pass.
      setTimeout(() => {
        const box = type === 'strength' ? this.strengthDisagreeBox : this.weaknessDisagreeBox;
        this.placeDisagreeBox(type, box?.nativeElement);

        // Put the caret in the box so the user can type straight away.
        // `preventScroll` — the browser's own focus scroll would fight the
        // placement above (it jumps the pane to the textarea).
        const input = type === 'strength' ? this.strengthDisagreeInput : this.weaknessDisagreeInput;
        input?.nativeElement.focus({ preventScroll: true });
      });
    }
  }

  closeDisagreeInput(type: 'strength' | 'weakness'): void {
    this.showDisagreeInput[type] = false;
    this.disagreeReason[type] = '';
  }

  /** Keep the reason box inside the visible area.
   *
   *  The box is absolutely positioned under its Disagree button, and the
   *  Strengths / Gaps cards sit at the bottom of the AI tab — so on a normal
   *  scroll position it opened past the clipped bottom edge of the pane and
   *  only its header was reachable. Measure once on open: flip it above the
   *  button when the space below can't hold it, and fall back to scrolling it
   *  into view when neither side fits. */
  private placeDisagreeBox(type: 'strength' | 'weakness', box?: HTMLElement | null): void {
    this.disagreeDropUp[type] = false;
    if (!box || !box.parentElement) return;

    const GAP = 8;
    const clipper = this.clippingParent(box);
    const bounds = clipper
      ? clipper.getBoundingClientRect()
      : ({ top: 0, bottom: window.innerHeight } as DOMRect);

    // The tab strip is sticky, so when the slider itself is the scroller the
    // strip floats over the top of the content — its bottom edge, not the
    // container's, is the real ceiling for a flipped box.
    let ceiling = bounds.top;
    const tabs = box.closest('.jc-v3-cps-slider, .jc-v3-jps-slider')
      ?.querySelector('.jc-v3-cps-tabs') as HTMLElement | null;
    if (tabs) ceiling = Math.max(ceiling, tabs.getBoundingClientRect().bottom);

    const rect = box.getBoundingClientRect();          // measured dropped-down
    const anchor = box.parentElement.getBoundingClientRect();

    if (rect.bottom <= bounds.bottom - GAP) return;    // fits below — leave it
    if (anchor.top - GAP - rect.height >= ceiling) {
      this.disagreeDropUp[type] = true;
      return;
    }
    box.scrollIntoView({ block: 'nearest' });
  }

  /** Nearest ancestor that visually clips its overflow — the edge the reason
   *  box has to stay inside. Returns null when nothing clips (page scroll). */
  private clippingParent(el: HTMLElement): HTMLElement | null {
    let node = el.parentElement;
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay' || overflowY === 'hidden') {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  /** Submit a DISAGREE with the typed reason. No-op on empty text. */
  submitDisagree(source: 'STRENGTH' | 'WEAKNESS', type: 'strength' | 'weakness'): void {
    const reason = this.disagreeReason[type];
    if (!reason || reason.trim() === '') return;
    this.showDisagreeInput[type] = false;
    this.sendCategoryFeedback(source, 'DISAGREE', reason.trim());
    this.disagreeReason[type] = '';
  }

  trackByIndex(i: number): number { return i; }
}
